import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { TrendingUp, Zap } from 'lucide-react';

const withoutApp = [32, 35, 34, 37, 36, 38, 37, 39, 38, 40, 39, 41];
const withApp = [32, 38, 42, 48, 52, 58, 62, 66, 70, 74, 78, 82];
const months = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

function AnimatedBar({ value, maxVal, delay, color, label }) {
  const height = (value / maxVal) * 100;
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <div className="relative w-full h-[140px] sm:h-[180px] flex items-end justify-center">
        <motion.div
          initial={{ height: 0 }}
          whileInView={{ height: `${height}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay, ease: 'easeOut' }}
          className={`w-full max-w-[24px] sm:max-w-[32px] rounded-t-md ${color}`}
        />
      </div>
      <span className="text-[#A0A0A0] text-[10px] sm:text-xs">{label}</span>
    </div>
  );
}

export default function ProgressionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = 2;
    const timer = setInterval(() => {
      start += 0.1;
      if (start >= end) {
        setCount(2);
        clearInterval(timer);
      } else {
        setCount(parseFloat(start.toFixed(1)));
      }
    }, 50);
    return () => clearInterval(timer);
  }, [isInView]);

  return (
    <section data-testid="progression-section" className="py-24 sm:py-32 relative overflow-hidden" ref={ref}>
      {/* Subtle bg glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF6B35]/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/20 mb-6">
            <Zap size={16} className="text-[#FF6B35]" />
            <span className="text-[#FF6B35] text-sm font-semibold font-['Poppins']">Progression prouvée</span>
          </div>

          <h2 className="text-white font-['Poppins'] font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">
            Les tireurs utilisant ClayPulse progressent{' '}
            <span className="relative inline-block">
              <motion.span
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5, type: 'spring', stiffness: 200 }}
                className="gradient-text font-black"
              >
                {count}x plus vite
              </motion.span>
            </span>
          </h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-[#A0A0A0] text-base sm:text-lg mt-4 max-w-xl mx-auto"
          >
            Suivre ses performances, analyser ses faiblesses et ajuster son entraînement : la clé de la progression au ball-trap.
          </motion.p>
        </motion.div>

        {/* Chart area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-[#1A1A1A] rounded-2xl border border-white/5 p-6 sm:p-8">
            {/* Chart header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-white font-['Poppins'] font-semibold text-base">Évolution du score moyen</h3>
                <p className="text-[#A0A0A0] text-xs mt-1">Score sur 25 plateaux — 12 mois</p>
              </div>
              <TrendingUp size={20} className="text-[#FF6B35]" />
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#FF6B35]" />
                <span className="text-[#A0A0A0] text-xs">Avec ClayPulse</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-white/15" />
                <span className="text-[#A0A0A0] text-xs">Sans suivi</span>
              </div>
            </div>

            {/* Bars chart */}
            <div className="flex gap-1 sm:gap-2">
              {months.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div className="relative w-full h-[140px] sm:h-[180px] flex items-end justify-center gap-[2px]">
                    {/* Without app bar */}
                    <motion.div
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${(withoutApp[i] / 85) * 100}%` } : {}}
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.04 }}
                      className="w-[6px] sm:w-[10px] rounded-t-sm bg-white/10"
                    />
                    {/* With app bar */}
                    <motion.div
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${(withApp[i] / 85) * 100}%` } : {}}
                      transition={{ duration: 0.6, delay: 0.15 + i * 0.04 }}
                      className="w-[6px] sm:w-[10px] rounded-t-sm bg-gradient-to-t from-[#FF6B35] to-[#FF8A5B]"
                    />
                  </div>
                  <span className="text-[#A0A0A0] text-[9px] sm:text-[10px] mt-1">{m}</span>
                </div>
              ))}
            </div>

            {/* Result highlight */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="mt-6 pt-5 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <TrendingUp size={20} className="text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">+50 plateaux/25 en 12 mois</p>
                  <p className="text-[#A0A0A0] text-xs">avec ClayPulse vs +8 sans suivi</p>
                </div>
              </div>
              <span className="px-4 py-2 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold font-['Poppins']">
                +156% de progression
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
